import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, UserIcon } from 'lucide-react';
import { Highlight } from './Heading';
import { SectionHeader } from './SectionHeader';
import { Reveal } from './Reveal';
import { testimonials } from '../data/testimonials';
const MEGAPHONE_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/f1a7ea40-b499-4448-97cf-dfd31fee876d.jpg';
export function TestimonialsSection() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const go = (dir: number) => setIndex(i => (i + dir + testimonials.length) % testimonials.length);
  useEffect(() => {
    if (paused) return;
    const id = window.setTimeout(() => go(1), 7000);
    return () => window.clearTimeout(id);
  }, [index, paused]);
  const t = testimonials[index];
  return <section id="referencias" className="relative overflow-hidden bg-primary py-12 md:py-14" data-id="element-393" data-mp="32gixvcjk4vjk">
      <div className="mx-auto max-w-6xl px-6" data-id="element-394" data-mp="2v1b08w6o5uny">
        <SectionHeader tone="inverse" eyebrow="Referencias" title={<>
              Voces que <Highlight tone="accent" data-id="element-396" data-mp="2q0eued2rbyfw">nos respaldan</Highlight>
            </>} data-id="element-395" data-mp="2v09lohqg2q1h" />

        <div className="mt-6 grid items-center gap-4 lg:grid-cols-[0.55fr_1.45fr] lg:gap-0" data-id="element-397" data-mp="34tgnxtpw1pxc">
          {/* Megaphone + sound waves */}
          <Reveal className="relative mx-auto w-full max-w-[220px] lg:max-w-[300px]" data-id="element-398" data-mp="2c26upxec6j11">
            <motion.img src={MEGAPHONE_IMAGE} alt="Megáfono que amplifica la voz de los empresarios" animate={{
            rotate: [0, -2, 0, 1.5, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="relative z-10 w-full" style={{
            WebkitMaskImage: 'radial-gradient(circle at 50% 50%, #000 55%, transparent 72%)',
            maskImage: 'radial-gradient(circle at 50% 50%, #000 55%, transparent 72%)'
          }} data-id="element-399" data-mp="273vijg0kjwfn" />
            {/* Waves coming out of the bell */}
            <div aria-hidden className="pointer-events-none absolute left-[68%] top-[37%] z-0 h-0 w-0" data-id="element-400" data-mp="8dlmiwmnja9b">
              {[0, 1, 2].map(n => <motion.span key={n} className="absolute -left-16 -top-16 h-32 w-32 rounded-full border-[3px] border-accent-gold" style={{
              clipPath: 'inset(0 0 0 50%)'
            }} initial={{
              scale: 0.6,
              opacity: 0
            }} animate={{
              scale: [0.6, 2.4],
              opacity: [0.9, 0]
            }} transition={{
              duration: 2.4,
              repeat: Infinity,
              delay: n * 0.8,
              ease: 'easeOut'
            }} data-id="element-401" data-mp="ddhray36b5px" />)}
            </div>
          </Reveal>

          {/* Speech bubble */}
          <Reveal delay={0.15} data-id="element-402" data-mp="2y3ffit0sjep2">
            <div className="relative" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-id="element-403" data-mp="2y24bcs3fo4xf">
              <span aria-hidden className="absolute -left-3 top-1/2 hidden h-8 w-8 -translate-y-1/2 rotate-45 rounded-sm bg-white lg:block" data-id="element-404" data-mp="1k7nx4je38ggn" />
              <div className="relative rounded-[28px] bg-white p-6 shadow-lg sm:p-8" data-id="element-405" data-mp="1b6hfu83m71mc">
                <div className="flex items-center justify-between" data-id="element-406" data-mp="1b8amvoi53nuv">
                  <span className="font-display text-[88px] font-extrabold leading-[0.6] text-accent-gold" aria-hidden data-id="element-407" data-mp="166uxxlcucx77">
                    “
                  </span>
                  <span className="font-display text-sm font-bold tabular-nums text-primary/40" data-id="element-408" data-mp="whbeadhb0zba">
                    {String(index + 1).padStart(2, '0')} / {String(testimonials.length).padStart(2, '0')}
                  </span>
                </div>

                <div className="relative mt-3 min-h-[160px] sm:min-h-[130px]" aria-live="polite" data-id="element-409" data-mp="neshaa079gwy">
                  <AnimatePresence mode="wait" data-id="element-410" data-mp="ijwk6vg3w3cz">
                    <motion.figure key={t.id} initial={{
                    opacity: 0,
                    scale: 0.96,
                    filter: 'blur(6px)'
                  }} animate={{
                    opacity: 1,
                    scale: 1,
                    filter: 'blur(0px)'
                  }} exit={{
                    opacity: 0,
                    scale: 1.03,
                    filter: 'blur(6px)'
                  }} transition={{
                    duration: 0.45,
                    ease: [0.22, 1, 0.36, 1]
                  }} data-id="element-411" data-mp="difnlc4nn327">
                      <blockquote className="font-display text-xl font-semibold leading-snug text-primary sm:text-2xl" data-id="element-412" data-mp="28t6xedxe5918">
                        {t.quote}
                      </blockquote>
                      <figcaption className="mt-5 flex items-center gap-3" data-id="element-413" data-mp="1zttw48jf7qrl">
                        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-gold text-white" data-id="element-414" data-mp="1kwjaw7gz76io">
                          <UserIcon className="h-5 w-5" data-id="element-415" data-mp="1pwfguc98h80v" />
                        </span>
                        <span data-id="element-416" data-mp="3f9smq5wzv443">
                          <span className="block text-base font-bold text-foreground" data-id="element-417" data-mp="3fau22ml1mcrm">{t.author}</span>
                          <span className="block text-sm text-foreground/60" data-id="element-418" data-mp="3k8vlx9qamg45">{t.role}</span>
                        </span>
                      </figcaption>
                    </motion.figure>
                  </AnimatePresence>
                </div>

                <div className="mt-6 flex items-center justify-between gap-6 border-t border-primary/10 pt-5" data-id="element-419" data-mp="1ible2wq5fwfj">
                  <div className="flex gap-2" data-id="element-420" data-mp="1dkmwzuxc6ub8">
                    {testimonials.map((x, i) => <button key={x.id} type="button" onClick={() => setIndex(i)} aria-label={`Ver referencia ${i + 1}`} aria-current={i === index} className={`h-2 rounded-full transition-all ${i === index ? 'w-8 bg-accent-gold' : 'w-2 bg-primary/20 hover:bg-primary/40'}`} data-id="element-421" data-mp="1rc08cll3clv6" data-mpi={i} />)}
                  </div>
                  <div className="flex gap-2" data-id="element-422" data-mp="1zg1f6q1sazj">
                    <button type="button" onClick={() => go(-1)} aria-label="Referencia anterior" className="flex h-11 w-11 items-center justify-center rounded-full border border-primary/15 text-primary transition hover:bg-primary hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" data-id="element-423" data-mp="2204jzdlytaou">
                      <ChevronLeftIcon className="h-5 w-5" data-id="element-424" data-mp="2kxnw4q3pz9fh" />
                    </button>
                    <button type="button" onClick={() => go(1)} aria-label="Referencia siguiente" className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-white transition hover:bg-secondary-maroon focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" data-id="element-425" data-mp="2px0eoynk5uc9">
                      <ChevronRightIcon className="h-5 w-5" data-id="element-426" data-mp="38pcr6e7xec04" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>;
}